import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const SerialNumberForm = NiceModal.create(({ id, serialNumberList, isRequired, hasUnique, quantity, onSuccess }) => {
	const modal = useModal();
	const { t } = useTranslation();
	const { showError, showSuccess } = useAlert();
	const axios = useAxiosPrivate();

	const handleCloseModal = () => {
		modal.remove();
	};

	const schema = useMemo(
		() =>
			yup.object().shape({
				serialNumbers: yup.array().of(
					yup.object().shape({
						value: yup
							.string()
							.nullable()
							.when([], {
								is: () => isRequired,
								then: (schema) => schema.required(),
								otherwise: (schema) => schema
							})
					})
				)
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			serialNumbers: serialNumberList
		}
	});

	const {
		control,
		handleSubmit,
		setError,
		watch,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading, errors }
	} = formMethods;

	const { fields, append: appendList, remove: removeList } = useFieldArray({ control, name: 'serialNumbers' });

	useEffect(() => {
		removeList();
		if (serialNumberList?.length > 0) {
			serialNumberList.forEach((item) => {
				appendList({ value: item });
			});
		} else {
			for (let i = 0; i < quantity; i += 1) {
				appendList({ value: '' });
			}
		}
		reset(watch());
	}, [serialNumberList, quantity]);

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const onSubmit = async (data) => {
		if (hasUnique) {
			const values = data.serialNumbers.map((s) => s.value);
			const duplicates = values.filter((val, idx, arr) => arr.indexOf(val) !== idx);

			values.forEach((val, index) => {
				if (duplicates.includes(val)) {
					setError(`serialNumbers.${index}.value`, {
						type: 'manual',
						message: 'serialNumberMustBeUnique'
					});
				}
			});
			if (duplicates.length > 0) {
				return;
			}
		}

		try {
			const parsedValue = data.serialNumbers.map((item) => item.value).map((item) => (item === null ? '' : item));
			const response = await axios.post(ORDER_ACTIONS.UPDATE_SERIAL_NUMBER_BY_ITEM_ID, {
				itemId: id,
				serialNumbers: parsedValue
			});
			if (response?.status === 200) {
				onSuccess(parsedValue);
				showSuccess();
				handleCloseModal();
			} else {
				throw new Error(response?.data?.message);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal maxWidth="x-small" fullwidth className="modal-serial-number-form">
			<ModalTitle>
				<Typography variant="p1">addSerialNumber</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless>
					{fields.map((item, index) => (
						<FormItem
							key={item.id}
							label={`${t('serialNumber')} ${index + 1}`}
							isLoading={isLoading}
							required={isRequired}>
							<StringInput
								name={`serialNumbers[${index}].value`}
								errorMessage={errors?.serialNumbers?.message}
							/>
						</FormItem>
					))}
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					label="save"
					onClick={handleSubmit(onSubmit, (error) => AppLogger.error(error))}
					variant="blue"
					loading={isLoading}
					disabled={!isDirty}
				/>
			</ModalActions>
		</Modal>
	);
});

export default SerialNumberForm;
